import { Button, ButtonProps, Col, ColProps, Space } from "@p0security/ui";
import classNames from "classnames";
import { CSSProperties, PropsWithChildren } from "react";

type SpaceProps = React.ComponentProps<typeof Space>;

/** The full-height create-account page: the sign-up panel and the logos below
 * it are pushed to opposite ends of the viewport. */
export const StyledContainer: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="relative flex h-screen flex-col justify-between overflow-y-auto">
    {children}
  </div>
);

/** A page column. The padding is marked important because antd's grid sets its
 * own inline padding from the row's gutter. */
export const StyledSectionCol: React.FC<ColProps> = ({
  className,
  ...props
}) => <Col className={classNames("px-8! py-4!", className)} {...props} />;

/** The marketing panel beside the sign-up form. */
export const StyledCompanyContent: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex flex-col px-12 py-32 text-lg [@media(max-width:992px)]:px-4 [@media(max-width:992px)]:py-20 [&_.bg-gradient]:absolute [&_.bg-gradient]:right-0 [&_.bg-gradient]:bottom-0 [&_.bg-gradient]:-ml-[10px] [&_.ant-list]:pt-4! [&_.ant-list_.ant-list-item]:mb-[10px]! [&_.ant-list_.ant-list-item]:border-none!">
    {children}
  </div>
);

/** One bullet in the marketing panel: a brand-colored check, then its text. */
export const StyledListContent: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex items-center [&_span]:pr-[10px]! [&_span]:text-p0-brand! [&_.ant-typography]:mb-0! [&_.ant-typography]:text-lg! [&_.ant-typography]:leading-6!">
    {children}
  </div>
);

/** The row of integration logos under the marketing copy. */
export const StyledIntegrationLogo: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex items-center justify-start gap-5 py-2 pr-2 pl-12">
    {children}
  </div>
);

/** The brand-colored column holding the sign-up form. */
export const LoginSectionCol: React.FC<ColProps> = ({
  className,
  ...props
}) => (
  <Col
    className={classNames("grid bg-p0-brand p-4! text-center", className)}
    {...props}
  />
);

/** The sign-up form itself, centered and capped to a readable width.
 *
 * `style` is passed through so `<Spacing asChild>` can place it. */
export const StyledMainContent: React.FC<
  PropsWithChildren<{ style?: CSSProperties }>
> = ({ children, style }) => (
  <div
    className="flex max-w-[350px] flex-col items-center justify-center"
    style={style}
  >
    {children}
  </div>
);

/** The "already have an account?" line above the form. On narrow screens the
 * logo appears here instead of in the marketing panel, which is hidden. */
export const StyledLoginLink: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="flex h-10 flex-col items-center pt-4 text-center text-base [&_a]:pl-[5px]! [&_a]:text-black/85! [&_.ant-image]:hidden! max-md:[&_.ant-image]:block! max-md:[&_.login-text]:pt-2">
    {children}
  </div>
);

/** Sizes the form's inputs and buttons alike, and renders validation errors in
 * the deeper red the page uses rather than antd's default. */
export const StyledButtonSpacing: React.FC<SpaceProps> = ({
  className,
  ...props
}) => (
  <Space
    className={classNames(
      "p-0 text-left",
      "[&_button]:my-2! [&_button]:w-[340px]! [&_button]:max-w-[340px]! [&_button:hover]:text-[#1677ff]!",
      "[&_input]:mb-4! [&_input]:w-[340px]! [&_input]:max-w-[340px]!",
      "[&_.ant-input-status-error]:border-[#8d0204]!",
      "[&_.ant-form-item-explain-error]:-mt-[0.7rem]! [&_.ant-form-item-explain-error]:text-[#8d0204]!",
      "[&_.ant-divider-inner-text]:text-[#2d2d2d]!",
      className
    )}
    {...props}
  />
);

/** The privacy notice under the sign-up buttons. */
export const StyledPrivacy: React.FC<PropsWithChildren> = ({ children }) => (
  <p className="text-[#2d2d2d] [&_a]:pl-[5px]! [&_a]:text-[#3b3b3b]! [&_a]:underline! [&_a:hover]:text-[#111]!">
    {children}
  </p>
);

/** The partner logos at the foot of the page. */
export const StyledLogos: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="flex items-center justify-center [&_img:first-child]:pr-4!">
    {children}
  </div>
);

/** Sign up with email. Important modifiers stand in for the original's doubled
 * `&&` selector, which existed to outrank antd's own button colors. */
export const EmailButton: React.FC<ButtonProps> = ({ className, ...props }) => (
  <Button
    className={classNames(
      "border-p0-surface-dark! bg-p0-surface-dark! text-white!",
      className
    )}
    {...props}
  />
);

/** The final "create account" action. */
export const CreateAccountButton: React.FC<ButtonProps> = ({
  className,
  ...props
}) => (
  <Button
    className={classNames(
      "-mt-4! w-[350px] max-w-[350px] border-[rgb(44,53,66)] bg-[rgb(44,53,66)] text-white",
      "hover:border-transparent hover:bg-[rgb(61,69,84)] hover:text-white!",
      "focus:border-transparent focus:bg-[rgb(61,69,84)] focus:text-white!",
      className
    )}
    {...props}
  />
);

/** Explains what the organization ID will be used for.
 *
 * `style` is passed through so `<Spacing asChild>` can place it. */
export const StyledOrgPointerText: React.FC<
  PropsWithChildren<{ style?: CSSProperties }>
> = ({ children, style }) => (
  <p className="pt-2 pb-4 text-[#2d2d2d]" style={style}>
    {children}
  </p>
);
